import { create } from 'zustand';
import api from '../services/api';
import { getAcquisition, getReferralCode } from '../services/acquisition';
import trackEvent from '../services/trackEvent';

const KEEPALIVE_INTERVAL = 30 * 60 * 1000; // 30 minutes

let keepaliveTimer = null;

function startKeepalive(token) {
    stopKeepalive();
    if (!token) return;
    keepaliveTimer = setInterval(async () => {
        try {
            const { data } = await api.post('/auth/keepalive.php');
            if (data.data?.token) {
                localStorage.setItem('monquiz_token', data.data.token);
                useAuthStore.setState({ token: data.data.token });
            }
        } catch {
            // 401 gere par l'interceptor
        }
    }, KEEPALIVE_INTERVAL);
}

// Visites réelles (migration 179) : le keepalive ne dit que « un onglet est ouvert ».
// Une visite = appli chargée connectée, connexion, ou retour au premier plan après
// 30 min d'absence. Événement user_events.app_opened (source chargement|connexion|retour).
const AWAY_MS = 30 * 60 * 1000;
let hiddenAt = null;
// Appareil de la visite (meta.device mobile|tablet|desktop + largeur de fenêtre meta.w + thème meta.theme
// light|dark) : part mobile / ordinateur, et part du mode sombre.
function deviceMeta() {
    try {
        const ua = navigator.userAgent || '';
        const tablet = /iPad|Tablet|Android(?!.*Mobile)/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
        const mobile = !tablet && /Mobi|iPhone|iPod|Android/i.test(ua);
        // Même règle que themeStore (choix mémorisé, sinon préférence système) : au chargement,
        // cet événement peut partir avant que le thème soit appliqué à la page.
        const stored = localStorage.getItem('monquiz_theme');
        const theme = stored === 'dark' || stored === 'light' ? stored
            : (document.documentElement.classList.contains('dark') || window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
        return { device: tablet ? 'tablet' : mobile ? 'mobile' : 'desktop', w: Math.round(window.innerWidth || 0), theme };
    } catch {
        return undefined;
    }
}
function trackVisit(source) {
    if (useAuthStore.getState().token) trackEvent('app_opened', { source, meta: deviceMeta() });
}
// Un navigateur mobile charge parfois la page sans qu'on la regarde (onglet restauré
// en arrière-plan à la réouverture du navigateur, préchargement depuis la barre
// d'adresse) : le « chargement » n'est compté qu'une fois la page réellement visible.
let pendingLoadVisit = false;
function trackLoadVisit() {
    if (document.prerendering || document.visibilityState !== 'visible') { pendingLoadVisit = true; return; }
    pendingLoadVisit = false;
    trackVisit('chargement');
}
if (typeof document !== 'undefined') {
    document.addEventListener('prerenderingchange', () => { if (pendingLoadVisit) trackLoadVisit(); });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
        if (pendingLoadVisit) { trackLoadVisit(); hiddenAt = null; return; }
        if (hiddenAt && Date.now() - hiddenAt >= AWAY_MS) trackVisit('retour');
        hiddenAt = null;
    });
}

function stopKeepalive() {
    if (keepaliveTimer) {
        clearInterval(keepaliveTimer);
        keepaliveTimer = null;
    }
}

const useAuthStore = create((set, get) => ({
    user: JSON.parse(localStorage.getItem('monquiz_user') || 'null'),
    token: localStorage.getItem('monquiz_token') || null,
    dashboardPrefs: JSON.parse(localStorage.getItem('monquiz_dashboard_prefs') || 'null'),
    impersonating: JSON.parse(localStorage.getItem('monquiz_impersonate') || 'null'),
    adminGate: null, // { active, passed } — état du portail admin (rempli par fetchProfile)
    loading: false,
    error: null,

    get isAuthenticated() {
        return !!get().token;
    },

    login: async (email, password) => {
        set({ loading: true, error: null });
        try {
            const { data } = await api.post('/auth/login.php', { email, password });
            const { token, user, dashboard_prefs, resume_scope } = data.data;
            localStorage.setItem('monquiz_token', token);
            localStorage.setItem('monquiz_user', JSON.stringify(user));
            if (dashboard_prefs) {
                localStorage.setItem('monquiz_dashboard_prefs', JSON.stringify(dashboard_prefs));
            }
            set({ user, token, dashboardPrefs: dashboard_prefs || null, loading: false });
            startKeepalive(token);
            trackVisit('connexion');
            // onboarded !== false → déjà vu l'accueil guidé (ou backend pas encore déployé).
            return { success: true, onboarded: user?.onboarded !== false, resumeScope: resume_scope || null };
        } catch (err) {
            const message = err.response?.data?.message || 'Erreur de connexion';
            const errors = err.response?.data?.errors || [];
            const code = errors.find(e => e?.code)?.code || null;
            set({ loading: false, error: message });
            return { success: false, message, code };
        }
    },

    // `acquisitionSource` = tag ?from= de la page d'où vient l'inscription. Sans
    // lui, un inscrit Google arrivé d'une landing serait tagué « google » et
    // perdrait sa pré-sélection de thématique (cf. lib/Acquisition.php).
    loginWithGoogle: async (credential, acquisitionSource = null) => {
        set({ loading: true, error: null });
        try {
            const body = { credential };
            if (acquisitionSource) body.acquisition_source = acquisitionSource;
            const referralCode = getReferralCode();
            if (referralCode) body.referral_code = referralCode;
            // Acquisition first-touch (utm_* + referrer), comme l'inscription classique.
            const acq = getAcquisition();
            if (acq) {
                if (acq.utm_source) body.utm_source = acq.utm_source;
                if (acq.utm_medium) body.utm_medium = acq.utm_medium;
                if (acq.utm_campaign) body.utm_campaign = acq.utm_campaign;
                if (acq.referrer) body.referrer = acq.referrer;
                if (acq.landing_page) body.landing_page = acq.landing_page;
            }
            const { data } = await api.post('/auth/google.php', body);
            const { token, user, dashboard_prefs, resume_scope } = data.data;
            localStorage.setItem('monquiz_token', token);
            localStorage.setItem('monquiz_user', JSON.stringify(user));
            if (dashboard_prefs) {
                localStorage.setItem('monquiz_dashboard_prefs', JSON.stringify(dashboard_prefs));
            }
            set({ user, token, dashboardPrefs: dashboard_prefs || null, loading: false });
            startKeepalive(token);
            trackVisit('connexion');
            // onboarded !== false → déjà vu l'accueil guidé (ou backend pas encore déployé).
            return { success: true, onboarded: user?.onboarded !== false, resumeScope: resume_scope || null };
        } catch (err) {
            const message = err.response?.data?.message || 'Erreur de connexion Google';
            set({ loading: false, error: message });
            return { success: false, message };
        }
    },

    register: async (email, password, prenom, acquisitionSource = null) => {
        set({ loading: true, error: null });
        try {
            const body = { email, password, prenom };
            if (acquisitionSource) body.acquisition_source = acquisitionSource;
            const referralCode = getReferralCode();
            if (referralCode) body.referral_code = referralCode;
            // Merge first-touch acquisition signals (utm_* + referrer) for attribution.
            const acq = getAcquisition();
            if (acq) {
                if (acq.utm_source) body.utm_source = acq.utm_source;
                if (acq.utm_medium) body.utm_medium = acq.utm_medium;
                if (acq.utm_campaign) body.utm_campaign = acq.utm_campaign;
                if (acq.referrer) body.referrer = acq.referrer;
                if (acq.landing_page) body.landing_page = acq.landing_page;
            }
            const { data } = await api.post('/auth/register.php', body);
            set({ loading: false });
            return { success: true, message: data.message };
        } catch (err) {
            const message = err.response?.data?.message || 'Erreur d\'inscription';
            const errors = err.response?.data?.errors || [];
            set({ loading: false, error: message });
            return { success: false, message, errors };
        }
    },

    resendVerification: async (email) => {
        try {
            const { data } = await api.post('/auth/resend-verification.php', { email });
            return { success: true, message: data.message };
        } catch {
            return { success: false, message: 'Erreur lors du renvoi' };
        }
    },

    // Kill-switch : invalide tous les JWT existants du compte (autres appareils/onglets)
    // et rafraîchit le token de l'appareil courant pour qu'il reste connecté.
    logoutAllSessions: async () => {
        try {
            const { data } = await api.post('/auth/logout-all.php');
            const newToken = data.data?.token;
            if (newToken) {
                localStorage.setItem('monquiz_token', newToken);
                set({ token: newToken });
                startKeepalive(newToken);
            }
            return { success: true, message: data.message };
        } catch (err) {
            return { success: false, message: err.response?.data?.message || 'Erreur' };
        }
    },

    // Marque l'accueil guidé comme vu (affiché une seule fois) côté serveur + local.
    markOnboarded: async () => {
        try { await api.post('/user/onboarded.php'); } catch { /* non bloquant */ }
        set((s) => {
            if (!s.user) return {};
            const u = { ...s.user, onboarded: true };
            localStorage.setItem('monquiz_user', JSON.stringify(u));
            return { user: u };
        });
    },

    pendingGifts: [],

    fetchProfile: async () => {
        try {
            const { data } = await api.get('/auth/me.php');
            const { user, subscriptions, dashboard_prefs, pending_gifts, admin_gate } = data.data;
            const fullUser = { ...user, subscriptions };
            localStorage.setItem('monquiz_user', JSON.stringify(fullUser));
            if (dashboard_prefs) {
                localStorage.setItem('monquiz_dashboard_prefs', JSON.stringify(dashboard_prefs));
            }
            set({ user: fullUser, dashboardPrefs: dashboard_prefs || null, pendingGifts: pending_gifts || [], adminGate: admin_gate || null });
        } catch {
            // 401 gere par l'interceptor
        }
    },

    clearPendingGift: (giftId) => {
        set(s => ({ pendingGifts: s.pendingGifts.filter(g => g.id !== giftId) }));
    },

    logout: () => {
        stopKeepalive();
        localStorage.removeItem('monquiz_token');
        localStorage.removeItem('monquiz_user');
        localStorage.removeItem('monquiz_dashboard_prefs');
        localStorage.removeItem('monquiz_impersonate');
        set({ user: null, token: null, dashboardPrefs: null, impersonating: null, error: null });
    },

    startImpersonation: async (targetUser) => {
        try {
            // Tell backend to start impersonation (session-based)
            await api.post('/admin/impersonate.php', { action: 'start', user_id: targetUser.id });
            const imp = { userId: targetUser.id, email: targetUser.email, prenom: targetUser.prenom };
            localStorage.setItem('monquiz_impersonate', JSON.stringify(imp));
            set({ impersonating: imp });
            // Fetch impersonated user's profile
            const { data } = await api.get('/auth/me.php');
            const { user, subscriptions, dashboard_prefs } = data.data;
            const fullUser = { ...user, subscriptions };
            localStorage.setItem('monquiz_user', JSON.stringify(fullUser));
            if (dashboard_prefs) {
                localStorage.setItem('monquiz_dashboard_prefs', JSON.stringify(dashboard_prefs));
            }
            set({ user: fullUser, dashboardPrefs: dashboard_prefs || null });
        } catch (e) { console.warn("Erreur:", e); }
    },

    stopImpersonation: async () => {
        try {
            // Tell backend to stop impersonation
            await api.post('/admin/impersonate.php', { action: 'stop' });
        } catch (e) { console.warn("Erreur:", e); }
        localStorage.removeItem('monquiz_impersonate');
        set({ impersonating: null });
        // Fetch admin's own profile
        try {
            const { data } = await api.get('/auth/me.php');
            const { user, subscriptions, dashboard_prefs } = data.data;
            const fullUser = { ...user, subscriptions };
            localStorage.setItem('monquiz_user', JSON.stringify(fullUser));
            if (dashboard_prefs) {
                localStorage.setItem('monquiz_dashboard_prefs', JSON.stringify(dashboard_prefs));
            }
            set({ user: fullUser, dashboardPrefs: dashboard_prefs || null });
        } catch (e) { console.warn("Erreur:", e); }
    },

    clearError: () => set({ error: null }),
}));

// Demarrer le keepalive si deja connecte au chargement
if (useAuthStore.getState().token) {
    startKeepalive(useAuthStore.getState().token);
    trackLoadVisit();
}

// Construit /connexion?reason=...&redirect=<current> en évitant les boucles
// (pas de redirect depuis /, /connexion, /inscription).
function buildLogoutRedirect(reason) {
    const path = window.location.pathname;
    const params = new URLSearchParams({ reason });
    if (path !== '/connexion' && path !== '/inscription' && path !== '/') {
        params.set('redirect', path + window.location.search + window.location.hash);
    }
    return '/connexion?' + params.toString();
}

// Ecouter les expirations de token
window.addEventListener('monquiz:auth_expired', () => {
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/connexion' && window.location.pathname !== '/inscription' && window.location.pathname !== '/') {
        window.location.href = buildLogoutRedirect('expired');
    }
});

// Ecouter les sessions remplacees (double connexion)
window.addEventListener('monquiz:session_replaced', () => {
    useAuthStore.getState().logout();
    if (window.location.pathname !== '/connexion') {
        window.location.href = buildLogoutRedirect('session_replaced');
    }
});

export default useAuthStore;
